/**
 * Quantidade em MILÉSIMOS (inteiro). 1 unidade = 1000.
 *
 * Mesma razão do dinheiro em centavos: 0,1 + 0,2 não fecha em ponto flutuante,
 * e saldo de almoxarifado que não fecha vira inventário perdido. Três casas
 * cobrem o que a obra usa — 12,5 sacos, 3,75 m³, 0,250 kg.
 */
export const QTY_SCALE = 1000;
/** 12500 => "12,5" (sem zeros à direita inúteis) */
export function formatQty(milli) {
    if (milli === null || milli === undefined || Number.isNaN(milli))
        return '—';
    return (milli / QTY_SCALE).toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
    });
}
/** 12500, 'SC' => "12,5 SC" */
export function formatQtyWithUnit(milli, unit) {
    const value = formatQty(milli);
    return value === '—' ? value : `${value} ${unit}`;
}
/** Converte o que o operador digitou ("12,5", "12.5", "1.200,75") em milésimos. */
export function parseQty(input) {
    if (input === null || input === undefined || input === '')
        return null;
    if (typeof input === 'number')
        return Math.round(input * QTY_SCALE);
    const cleaned = input.replace(/[^\d,.-]/g, '').trim();
    if (cleaned === '' || cleaned === '-')
        return null;
    const hasComma = cleaned.includes(',');
    const normalized = hasComma ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;
    const value = Number(normalized);
    if (Number.isNaN(value))
        return null;
    return Math.round(value * QTY_SCALE);
}
/**
 * Valor total de uma linha: quantidade (milésimos) × preço unitário (centavos).
 * Arredonda no fim, uma vez só — arredondar antes espalha erro por linha.
 */
export function lineTotalCents(quantityMilli, unitPriceCents) {
    return Math.round((quantityMilli * unitPriceCents) / QTY_SCALE);
}
/**
 * Custo médio ponderado após uma entrada.
 * Devolve o custo anterior quando o saldo resultante é zero ou negativo —
 * dividir por zero aqui produziria NaN gravado no banco.
 */
export function weightedAverageCents(currentQtyMilli, currentAvgCents, incomingQtyMilli, incomingUnitCents) {
    const totalQty = currentQtyMilli + incomingQtyMilli;
    if (totalQty <= 0)
        return currentAvgCents;
    const currentValue = currentQtyMilli * currentAvgCents;
    const incomingValue = incomingQtyMilli * incomingUnitCents;
    return Math.round((currentValue + incomingValue) / totalQty);
}
/**
 * Fator de conversão 1:1, em milésimos. A nota e o catálogo contam igual.
 */
export const NEUTRAL_FACTOR_MILLI = 1000;
/**
 * Converte quantidade e preço da unidade DA NOTA para a unidade DO CATÁLOGO.
 *
 * O fornecedor vende caixa, o almoxarifado conta unidade. `factorMilli` diz
 * quantas unidades do catálogo vêm em uma da nota, em milésimos: caixa de 12
 * é 12000, e 1:1 é 1000.
 *
 *   5 CX a R$ 240,00, fator 12  →  60 UN a R$ 20,00
 *
 * O preço sai do preço unitário DA NOTA dividido pelo fator, e não do total
 * da linha: com fator 1000 o resultado é idêntico à entrada, bit a bit, e uma
 * nota sem conversão continua valendo exatamente o que valia antes desta
 * função existir.
 *
 * Arredonda uma vez em cada número, como o resto do sistema. Fator que não
 * divide redondo (caixa de 3 a R$ 100,00) deixa até um centavo por unidade
 * para trás no CUSTO MÉDIO — o que se paga ao fornecedor é o total da nota e
 * não passa por aqui.
 */
export function convertToCatalogUnit(line, factorMilli) {
    return {
        quantityMilli: Math.round((line.quantityMilli * factorMilli) / QTY_SCALE),
        unitCostCents: Math.round((line.unitPriceCents * QTY_SCALE) / factorMilli),
    };
}
//# sourceMappingURL=quantity.js.map